import { useRef, useState, useCallback, forwardRef, useEffect } from "react";

import { GENERATE_PRESIGNED_URL_FOR_USER_UPLOAD_MUTATION } from "@/graphql/accounts";
import ChevronComponent from "@/tpo/Chevron";
import ButtonV2 from "@/v2/Buttons";
import { useMutation } from "@apollo/client";
import { captureException } from "@sentry/browser";
import styled from "styled-components";

import Box from "./Box";
import Loading from "./Loading";

export const FileInputWrapper = styled(Box)`
  position: relative;

  input[type="file"] {
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    top: 0;
  }
`;

function uploadFileToS3(url, fields, file) {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => formData.set(key, value));
  formData.set("file", file, fields.key);
  const request = new Request(url, {
    method: "POST",
    body: formData
  });
  return fetch(request);
}

export const INITIAL_STATUS = "initial";
export const UPLOADING_STATUS = "uploading";
export const SUCCESS_STATUS = "success";
export const ERROR_STATUS = "error";

export const DOCUMENT_FILE_ACCEPT = "application/pdf,image/jpeg,image/png,.jpeg,.jpg,.pdf,.png";

export const DOCUMENT_FILE_ACCEPT_HELP_TEXT = "Accepted file types: PDF, JPG, or PNG.";

export const IMAGE_FILE_ACCEPT = "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp";

export const IMAGE_FILE_ACCEPT_HELP_TEXT = "Accepted file types: JPG, PNG, or WEBP.";

export const INVALID_FILE_TYPE_MESSAGE =
  "This file type is not allowed. Please choose a file in an accepted format.";

/** HTML accept is not applied to drag-and-drop; validate before upload. */
export function isFileAllowed(file, accept) {
  if (!accept || !file) return true;

  const tokens = accept
    .split(",")
    .map(token => token.trim().toLowerCase())
    .filter(Boolean);
  const fileName = file.name.toLowerCase();
  const fileType = (file.type || "").toLowerCase();

  return tokens.some(token => {
    if (token.startsWith(".")) {
      return fileName.endsWith(token);
    }
    if (token.endsWith("/*")) {
      const prefix = token.slice(0, -1);
      return fileType.startsWith(prefix);
    }
    return fileType === token;
  });
}

// TODO - the UploadTest page should make use of this module
// but this PR will merged after the changes that have already been made to the UploadTest page
// on another PR

export function useUploadFileToS3({ publicBucket = false, onChange, accept }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [generatePresignedUrlMutation] = useMutation(
    GENERATE_PRESIGNED_URL_FOR_USER_UPLOAD_MUTATION
  );

  const [status, setStatus] = useState(INITIAL_STATUS);
  const [errorMessage, setErrorMessage] = useState(null);

  const upload = useCallback(
    async event => {
      const file = event.target.files?.[0];
      event.target.value = "";

      if (!file || status === UPLOADING_STATUS) return;

      setErrorMessage(null);

      if (accept && !isFileAllowed(file, accept)) {
        setStatus(ERROR_STATUS);
        setErrorMessage(INVALID_FILE_TYPE_MESSAGE);
        return;
      }

      setStatus(UPLOADING_STATUS);
      try {
        const resp = await generatePresignedUrlMutation({
          variables: { fileName: file.name, public: publicBucket }
        });
        const data = resp.data;
        if (
          data.generatePresignedUrlForUserUploadMutation.url &&
          data.generatePresignedUrlForUserUploadMutation.fields
        ) {
          const fields = data.generatePresignedUrlForUserUploadMutation.fields;
          const url = data.generatePresignedUrlForUserUploadMutation.url;
          const fileUrl = data.generatePresignedUrlForUserUploadMutation.fileUrl;
          // fileUrl will only exist if the bucket is public
          try {
            const s3UploadResp = await uploadFileToS3(url, fields, file);
            if (s3UploadResp.status === 204) {
              onChangeRef.current({
                key: fields.key,
                fileUrl: fileUrl,
                file
              });
              setStatus(SUCCESS_STATUS);
            } else {
              setStatus(ERROR_STATUS);
            }
          } catch (e) {
            setStatus(ERROR_STATUS);
            captureException(e);
            console.log("Error encountered uploading file to s3", e);
          }
        } else {
          // no presigned url so can't upload
          // not sure this can happen
          setStatus(ERROR_STATUS);
        }
      } catch (e) {
        setStatus(ERROR_STATUS);
        console.log(`Error encountered generating a presigned url for user test upload, ${e}`);
      }
    },
    [accept, generatePresignedUrlMutation, status, publicBucket]
  );

  return {
    status,
    upload,
    errorMessage
  };
}

export const UploadFileToS3 = forwardRef(
  ({ publicBucket = false, label, onChange, name, value, accept, ...rest }, ref) => {
    const { status, upload, errorMessage } = useUploadFileToS3({ publicBucket, onChange, accept });

    let rightIcon = <ChevronComponent />;
    if (status === UPLOADING_STATUS) {
      rightIcon = <Loading color="white" size={20} minHeight={0} minWidth={20} />;
    }

    return (
      <FileInputWrapper data-component-name="UploadFileToS3" {...rest}>
        <input
          ref={ref}
          type="file"
          accept={accept}
          onChange={upload}
          disabled={status === UPLOADING_STATUS}
          data-testid="hiddenFileInput"
        />
        <ButtonV2
          color="dark"
          rightIcon={rightIcon}
          type="button"
          width="100%"
          style={{
            pointerEvents: "none"
          }}
          size={["sm", "sm", "md"]}
        >
          {label}
        </ButtonV2>
        {errorMessage && (
          <Box fontFamily="gilroyMedium" fontSize={12} color="red" mt="8px" lineHeight="20px">
            {errorMessage}
          </Box>
        )}
      </FileInputWrapper>
    );
  }
);
